import React from 'react';
import InputBase from '@mui/material/InputBase';
import Box from '@mui/material/Box';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import commons from '../../../assets/styles/commons';
import colors from '../../../assets/styles/colors';
import { Control, Controller, UseControllerProps } from 'react-hook-form';
import { styled } from '@material-ui/core';
import InputAdornment from '@mui/material/InputAdornment';
import Typography from '../typography';

interface FormInputProps extends Omit<UseControllerProps, 'defaultValue'> {
  label?: string;
  name: string;
  id?: string;
  style?: React.CSSProperties;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  value?: number | string;
  defaultValue?: number | string;
  placeholder?: string;
  mask?: string;
  control?: Control<any>;
  errorMessage?: string | any;
  size?: 'small' | 'medium';
  startAdornment?: any;
  endAdornment?: any;
  onBlur?: () => void;
  maxLength?: number;
  type?: string;
}

const CustomInput = styled(InputBase)(({ theme }) => ({
  'label + &': {
    marginTop: theme.spacing(3),
    color: colors.neutral.gray,
  },

  '&.MuiInputBase-root ': {
    borderRadius: 4,
    position: 'relative',
    backgroundColor: colors.secondary.softBlue,
    border: '1px solid',
    borderColor: colors.primary.orange,
    flex: 1,
    width: 'auto',
    height: '18px',
    padding: '7px 12px',
    transition: theme.transitions.create([
      'border-color',
      'background-color',
      'box-shadow',
    ]),
    fontFamily: commons.fontFamily,
    fontVariant: 'lining-nums',
    '&:focus': {
      boxShadow: `rgba(255, 114, 6, 0.25) 0 0 0 0.2rem`,
      borderColor: colors.primary.orange,
    },
    '&.Mui-focused': {
      color: colors.primary.orange,
    },
  },

  '& .MuiInputBase-input': {
    padding: '0',
  },

  '& .MuiTypography-root': {
    fontFamily: commons.fontFamily,
    color: colors.primary.orange,
    fontSize: 18,
  },
  '& .MuiInputAdornment-root': {
    marginLeft: 0,
  },

  '& .MuiInputAdornment-positionStart': {
    marginRight: 0,
  },
}));

const ControllerInput: React.FC<FormInputProps> = ({
  label,
  id,
  style,
  placeholder,
  size = 'small',
  name,
  control,
  startAdornment,
  endAdornment,
  defaultValue,
  maxLength,
  disabled,
  type,
}) => {
  return (
    <Box component="div" sx={{ display: 'grid' }}>
      <Controller
        name={name || ''}
        control={control}
        render={({
          field: { ref, onBlur, onChange, value },
          fieldState: { error },
        }) => (
          <FormControl size={size}>
            <InputLabel
              variant="standard"
              shrink
              htmlFor={id}
              sx={{
                fontFamily: commons.fontFamily,
                color: colors.primary.orange,
                fontSize: 18,
                '&.Mui-focused': {
                  color: colors.primary.orange,
                },
                ...style,
              }}
            >
              {label}
            </InputLabel>
            <CustomInput
              value={value}
              defaultValue={defaultValue}
              onChange={onChange}
              id={id}
              placeholder={placeholder}
              ref={ref}
              onBlur={onBlur}
              type={type}
              inputProps={{ maxLength }}
              disabled={disabled}
              size={size}
              error={!!error?.message}
              endAdornment={
                <InputAdornment position="end">{endAdornment}</InputAdornment>
              }
              startAdornment={
                <InputAdornment position="start">
                  {startAdornment}
                </InputAdornment>
              }
            />

            {!!error && (
              <Box marginTop={0.5}>
                <Typography fontSize="xxs" color="error">
                  {error?.message}
                </Typography>
              </Box>
            )}
          </FormControl>
        )}
      />
    </Box>
  );
};

export default ControllerInput;
